import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';

// Helper function to convert minutes to duration string
const minutesToDurationString = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
};

// Helper function to convert duration string to minutes
const durationStringToMinutes = (durationString) => {
  const match = durationString.match(/(\d+)h\s*(\d+)?m?/);
  if (match) {
    const hours = parseInt(match[1]);
    const minutes = match[2] ? parseInt(match[2]) : 0;
    return hours * 60 + minutes;
  }
  return 0;
};

const DurationRangeSlider = ({
  label,
  startDuration,
  endDuration,
  onDurationChange,
  minDuration = 0,
  maxDuration = 1440, // 24 hours in minutes
  step = 15, // 15 minute steps
  mode = 'range' // 'range' | 'max'
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragHandle, setDragHandle] = useState(null);
  const sliderRef = useRef(null);

  const handleMouseDown = (e, handle) => {
    setIsDragging(true);
    setDragHandle(handle);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    const percentage = Math.max(0, Math.min(1, x / width));
    const newDuration = Math.round((minDuration + percentage * (maxDuration - minDuration)) / step) * step;

    if (mode === 'max') {
      // Single handle – adjust only the end (max) value; keep start at 0 for filtering
      const newEndDuration = Math.max(newDuration, minDuration + step);
      onDurationChange({ start: 0, end: newEndDuration });
    } else {
      if (dragHandle === 'start') {
        const newStartDuration = Math.min(newDuration, endDuration - step);
        onDurationChange({ start: newStartDuration, end: endDuration });
      } else if (dragHandle === 'end') {
        const newEndDuration = Math.max(newDuration, startDuration + step);
        onDurationChange({ start: startDuration, end: newEndDuration });
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragHandle(null);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragHandle, startDuration, endDuration, onDurationChange, minDuration, maxDuration, step]);

  const effectiveStart = mode === 'max' ? minDuration : startDuration;
  const startPercentage = ((effectiveStart - minDuration) / (maxDuration - minDuration)) * 100;
  const endPercentage = ((endDuration - minDuration) / (maxDuration - minDuration)) * 100;
  const rangeWidth = endPercentage - startPercentage;

  return (
    <Box sx={{ marginBottom: 3 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, marginBottom: 1, color: '#222' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: '#666', marginBottom: 1 }}>
        {/* Show fixed min/max like the reference UI */}
        <span>{minutesToDurationString(minDuration)}</span>
        <span>{minutesToDurationString(maxDuration)}</span>
      </Box>
      <Box
        ref={sliderRef}
        sx={{
          position: 'relative',
          height: 2,
          backgroundColor: '#e5e7eb',
          borderRadius: 3,
          margin: '0 10px',
          cursor: 'pointer'
        }}
      >
        {/* Range fill */}
        <Box sx={{
          position: 'absolute',
          left: `${startPercentage}%`,
          width: `${rangeWidth}%`,
          height: '100%',
          backgroundColor: '#0ea5b2',
          borderRadius: 3
        }} />

        {/* Start handle (hidden for max mode) */}
        {mode !== 'max' && (
          <Box
            sx={{
              position: 'absolute',
              left: `calc(${startPercentage}% - 10px)`,
              top: '-7px',
              width: 20,
              height: 20,
              backgroundColor: '#fff',
              border: '2px solid rgb(117, 125, 139)',
              borderRadius: '50%',
              boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
              cursor: 'grab',
              '&:active': {
                cursor: 'grabbing'
              }
            }}
            onMouseDown={(e) => handleMouseDown(e, 'start')}
          />
        )}

        {/* End handle */}
        <Box
          sx={{
            position: 'absolute',
            left: `calc(${endPercentage}% - 10px)`,
            top: '-7px',
            width: 20,
            height: 20,
            backgroundColor: '#fff',
            border: '2px solid rgb(124, 135, 152)',
            borderRadius: '50%',
            boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing'
            }
          }}
          onMouseDown={(e) => handleMouseDown(e, 'end')}
        />
      </Box>
    </Box>
  );
};

export default DurationRangeSlider; 